import { useEffect } from 'react';

/**
 * The unsaved-changes guard (§12, §25.5).
 *
 * Two exits have to be covered and they are covered by different mechanisms,
 * which is why this is a hook rather than a prop on a form:
 *
 *   - Leaving the page — closing the tab, reloading, following a link out of
 *     the app — is `beforeunload`. The browser writes the wording; nothing we
 *     pass it is displayed, which is why there is no message here to translate.
 *   - Navigating within the app is TanStack Router's `blocker`, wired at the
 *     route. That one we do word ourselves.
 *
 * Only armed when the form is actually dirty. A guard that fires on a form
 * nobody touched teaches people to dismiss it without reading, and then it
 * stops working on the form that mattered.
 */
export function useUnsavedChangesGuard(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;

    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Required by older browsers; the string is never shown.
      event.returnValue = '';
    };

    globalThis.addEventListener('beforeunload', warn);
    return () => globalThis.removeEventListener('beforeunload', warn);
  }, [isDirty]);
}
